import { Link } from 'react-router';

import {
  Card,
  ProgressBar,
  Tooltip,
  useBreakpoints,
  useMediaQuery,
} from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import Constants from 'constants/index';

import PlanCardCategoryIcon from './PlanCardContent/PlanCardCategoryIcon';

interface PlanCardProps {
  category?: string;
  children: React.ReactNode;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  link?: string | null;
  progress?: number | null;
  buttons?: React.ReactNode;
  tooltip?: string;
}

const PlanCard: React.FC<PlanCardProps> = ({
  category = Constants.PLAN_CATEGORIES.GENERAL,
  children,
  className,
  dataTestId = 'PlanCard',
  disabled,
  link,
  progress = 0,
  buttons,
  tooltip,
}) => {
  // Useful because this affects a value that is js-only and dependent on screen width
  const breakpoints = useBreakpoints();
  const isSmAndDown = useMediaQuery(breakpoints.down('sm'));

  const showProgressBar =
    category === Constants.PLAN_CATEGORIES.COMPLIANCE && disabled
      ? false
      : progress !== null && progress !== 100;

  const baseContent = (
    <div
      className={cn(
        'relative flex w-full max-w-[816px] flex-col items-start justify-center gap-2 p-4 pb-5',
        {
          'pb-4': category === Constants.PLAN_CATEGORIES.COMPLIANCE && disabled,
        },
      )}
    >
      <div
        className={cn(
          'flex w-full flex-row gap-2',
          buttons && showProgressBar && 'pb-2',
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col items-start text-left! md:flex-row md:items-center',
          )}
        >
          {children}
        </div>
      </div>
      {showProgressBar && (
        <div
          className={cn(
            'h-[5px]',
            buttons ? 'absolute bottom-3.5 w-[calc(100%+12px)]' : 'w-full',
          )}
        >
          <ProgressBar color="success" compact value={progress ?? 0} />
        </div>
      )}
    </div>
  );

  const cardContent = (
    <Card
      className={cn(
        'planCard relative flex h-auto w-full max-w-full animate-[fadeIn_linear_0.3s] flex-row! items-center justify-start p-0!',
        disabled && 'no-hover cursor-default',
        className,
      )}
      data-testid={dataTestId}
    >
      {/* 
        Most plan cards require a link but compliance plans inside of Reporting should not link you outside 
        of that page so we render those cards without a link.
      */}
      {link && !disabled && (
        <Link to={link} className="flex w-full">
          {!!category && (
            <PlanCardCategoryIcon category={category} disabled={!!disabled} />
          )}
          {baseContent}
        </Link>
      )}
      {/* 
        This is a specific case when in Manage Plans (and viewing a disabled compliance plan).
        We don't want them to link to another page in a disabled state but we also don't want the tooltip attached
      */}
      {((disabled ?? false) || !link) && (
        <div className="flex w-full">
          {!!category && (
            <PlanCardCategoryIcon
              category={category}
              disabled={!!disabled}
              disabledCompliance={
                category === Constants.PLAN_CATEGORIES.COMPLIANCE && disabled
              }
            />
          )}
          {baseContent}
        </div>
      )}

      {buttons && <div className="pr-6 pb-2">{buttons}</div>}
    </Card>
  );

  // Tooltip wraps the Compliance cards in Manage Plans
  return tooltip ? (
    <Tooltip
      content={tooltip}
      placement="bottom"
      showOnElementDelay={{
        showDelay: (!isSmAndDown && 500) as number,
      }}
    >
      {cardContent}
    </Tooltip>
  ) : (
    cardContent
  );
};

export default PlanCard;
